import { supabase } from "@/lib/supabase";
import type { Tables } from "@/types/database.types";
import { useCallback, useEffect, useState } from "react";

export type EmployeeAccessRow = Pick<
  Tables<"employees">,
  "id" | "name" | "nickname" | "email" | "office_id" | "is_user" | "is_active"
> & {
  office: { name: string | null } | null;
};

export interface UseEmployeesAccessDataResult {
  employees: EmployeeAccessRow[];
  loading: boolean;
  error: string | null;
  refresh: () => Promise<void>;
}

/**
 * Loads every employee with their office name for the /settings access table.
 * Read-only for now; the edit/save layer (local working state, dirty tracking,
 * guard rails) is added in a later stage.
 */
export function useEmployeesAccessData(): UseEmployeesAccessDataResult {
  const [employees, setEmployees] = useState<EmployeeAccessRow[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("employees")
      .select(
        `
        id,
        name,
        nickname,
        email,
        office_id,
        is_user,
        is_active,
        office:offices ( name )
      `
      )
      .eq("is_active", true)
      .order("name", { ascending: true });

    if (error) {
      setError(error.message ?? "Failed to load employees");
      setEmployees([]);
      setLoading(false);
      return;
    }

    setEmployees((data as EmployeeAccessRow[]) ?? []);
    setError(null);
    setLoading(false);
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  return { employees, loading, error, refresh };
}
